import { useSidebar } from '@/components/ui/sidebar'
import { Button } from '@/components/ui/button'
import { CirclePlus, PlusCircle } from 'lucide-react'
import { cn } from '@/lib/utils'
import { supabase } from '@/lib/supabaseClient'
import { useAuth } from '@/features/auth/auth-context'
import { useState } from 'react'
import { initiateYouTubeAuth } from '@/lib/youtube-auth'

type SchedulePostButtonProps = {
  variant?: 'sidebar' | 'default'
  className?: string
  onAllowSchedule: () => void // Called ONLY if YouTube is connected
}

export function SchedulePostButton({
  variant = 'default',
  className,
  onAllowSchedule,
}: SchedulePostButtonProps) {
  const { state } = useSidebar()
  const isCollapsed = state === 'collapsed'
  const { user } = useAuth()
  const [checking, setChecking] = useState(false)

  const handleClick = async () => {
    if (!user) return

    setChecking(true)

    try {
      const { data: session } = await supabase.auth.getSession()
      if (!session.session?.access_token) {
        alert('Session expired. Please sign in again.')
        setChecking(false)
        return
      }

      // ✅ Check if YouTube token exists
      const res = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/rest/v1/youtube_tokens?select=access_token&user_id=eq.${user.id}`,
        {
          headers: {
            apikey: import.meta.env.VITE_SUPABASE_ANON_KEY!,
            Authorization: `Bearer ${session.session.access_token}`,
            Accept: 'application/json',
          },
        }
      )

      if (res.ok) {
        const data = await res.json()
        const connected = !!data?.[0]?.access_token

        if (connected) {
          console.log('✅ YouTube connected, opening upload modal')
          onAllowSchedule()
        } else {
          console.log('❌ No YouTube connection, redirecting to OAuth')
          alert('You need to connect your YouTube account before scheduling a post.')
         redirectToYouTubeOAuth();
        }
      } else {
        console.error('❌ Failed to check YouTube connection')
        alert('Could not verify YouTube connection. Please try again.')
      }
    } catch (error) {
      console.error('❌ Error checking YouTube connection:', error)
      alert('An error occurred while checking your YouTube connection.')
    }

    setChecking(false)
  }

 const redirectToYouTubeOAuth = () => {
  console.log('🔄 Redirecting to YouTube OAuth via backend');
  initiateYouTubeAuth();
}

  const label = checking ? 'Checking YT connection...' : 'Schedule Post'
  
if (variant === 'sidebar') {
  return (
    <Button
      onClick={handleClick}
      disabled={checking}
      className={cn(
        'w-fill flex items-center gap-8 px-4',
        isCollapsed ? 'justify-center' : 'justify-start px-4',
        className
      )}
    >
<PlusCircle 
  style={{ width: '20px', height: '20px' }}
  className="flex-shrink-0" 
  fill="currentColor" 
  stroke="white"
  strokeWidth={2}
/>
      {!isCollapsed && <span className="text-sm font-medium">{label}</span>}
    </Button>
  )
}

  return (
    <Button
       size="lg"
      onClick={handleClick}
      disabled={checking}
      className={cn('w-fit flex items-center gap-8 px-4', className)}
    >
      <PlusCircle 
  style={{ width: '20px', height: '20px' }}
  className="flex-shrink-0" 
  fill="currentColor" 
  stroke="white"
  strokeWidth={2}
/>
      <span>{label}</span>
    </Button>
  )
}
